import { useEffect } from "react";

type Props = {
  title: string;
  description?: string;
  canonical?: string;
  image?: string;
  imageAlt?: string;
  noindex?: boolean;
};

const SITE = "Today's Brief";
const SITE_ORIGIN = "https://todaysbrief.app";

function absoluteUrl(value: string): string {
  return value.startsWith("http")
    ? value
    : `${SITE_ORIGIN}${value.startsWith("/") ? value : `/${value}`}`;
}

export function PageMeta({ title, description, canonical, image, imageAlt, noindex }: Props) {
  useEffect(() => {
    const resolvedTitle = title.includes(SITE) ? title : `${title} · ${SITE}`;
    const resolvedCanonical = canonical ? absoluteUrl(canonical) : "";
    const resolvedImage = image ? absoluteUrl(image) : "";
    document.title = resolvedTitle;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setProperty = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const removeMeta = (selector: string) => document.querySelector(selector)?.remove();

    if (description) {
      setMeta("description", description);
      setProperty("og:description", description);
      setMeta("twitter:description", description);
    } else {
      removeMeta('meta[name="description"]');
      removeMeta('meta[property="og:description"]');
      removeMeta('meta[name="twitter:description"]');
    }
    if (noindex) setMeta("robots", "noindex, nofollow");
    else setMeta("robots", "index, follow");
    setProperty("og:title", resolvedTitle);
    setMeta("twitter:title", resolvedTitle);
    setProperty("og:type", "website");
    setMeta("twitter:card", "summary_large_image");

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (resolvedCanonical) {
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = resolvedCanonical;
      setProperty("og:url", resolvedCanonical);
    } else {
      link?.remove();
      document.querySelector('meta[property="og:url"]')?.remove();
    }

    if (resolvedImage) {
      setProperty("og:image", resolvedImage);
      setProperty("og:image:width", "1200");
      setProperty("og:image:height", "630");
      setProperty("og:image:alt", imageAlt || `${SITE} product preview`);
      setMeta("twitter:image", resolvedImage);
      setMeta("twitter:image:alt", imageAlt || `${SITE} product preview`);
    } else {
      removeMeta('meta[property="og:image"]');
      removeMeta('meta[property="og:image:width"]');
      removeMeta('meta[property="og:image:height"]');
      removeMeta('meta[property="og:image:alt"]');
      removeMeta('meta[name="twitter:image"]');
      removeMeta('meta[name="twitter:image:alt"]');
    }
  }, [title, description, canonical, image, imageAlt, noindex]);

  return null;
}
